import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';

export interface CrmNote {
  noteId: number;
  clientId?: number;
  noteFrom?: number;
  noteTo?: number;
  noteDescr?: string;
  noteArchived?: boolean;
  noteArchivedDate?: string;
  noteArchivedBy?: number;
  noteCreatedAt?: string;
  noteCreatedBy?: number;
  noteUpdatedAt?: string;
  noteUpdatedBy?: number;
  // Related data from API includes
  client?: {
    clientName?: string;
  };
  noteFromUser?: {
    id?: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
  noteToUser?: {
    id?: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
  noteCreatedUser?: {
    id?: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
  noteUpdatedUser?: {
    id?: number;
    userFirstName?: string;
    userLastName?: string;
    userDisplayName?: string;
  };
}

export interface CrmNotesResponse {
  data: CrmNote[];
  total: number;
  page: number;
  limit: number;
}

export const useCrmNotes = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<CrmNote[]>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    crmNotes: data || [], // Keep for backward compatibility
    total: data?.length || 0,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useCrmNote = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<CrmNote>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    crmNote: data, // Keep for backward compatibility
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get users for select dropdown
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    users: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((user) => ({
      value: String(user.id),
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

